import React, { useRef, useState } from "react";
import {
  CustomFormField,
  CustomFormSelectField,
} from "components/custom-form/CustomForm";
import type { DropdownOption } from "components/searchable-dropdown/types";
import { useSelector } from "react-redux";
import card_services from "services/card_services";
import verification_services from "services/verification_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "utils/errors/parseApiError";
import { resolveToken, tokenLabel, tokensFor } from "utils/tokens";
import { useTokensSupport } from "features/tokens/useTokenSupport";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import {
  OTP_LENGTH,
  classifyOtpFailure,
  classifyPreflightFailure,
  classifySignupFailure,
  sanitizeOtp,
  useResendCountdown,
} from "hooks/useEmailOtp";

const EMAIL_RE = /\S+@\S+\.\S+/;

export type IssueCardStep = 1 | 2 | 3;

export interface UseIssueCardReturn {
  step: IssueCardStep;
  /** The flow is 2 or 3 steps depending on whether the email gate runs. */
  displayStep: number;
  totalSteps: number;
  fields: CustomFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  submitLabel: string;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  /** False when re-submitting cannot help — a dead code, or the guess cap. */
  canRetry: boolean;
  showSuccess: boolean;
  successMsg: string;
  /** Non-blocking note under the form, e.g. that a resend invalidated the previous code. */
  noticeMsg: string;
  /** True once the preflight said this card needs no email proof — see submitStep1. */
  skipsEmailGate: boolean;
  /** Resend affordance for step 2. `secondsLeft` counts down to 0, then `canResend` is true. */
  canResend: boolean;
  secondsLeft: number;
  handleResend: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  /** Retry the current step. Dismisses the error first — see the comment on handleRetry. */
  handleRetry: () => Promise<void>;
  handleCloseError: () => void;
  handleCloseSuccess: () => void;
  reset: () => void;
}

export function useIssueCard(): UseIssueCardReturn {
  const [step, setStep] = useState<IssueCardStep>(1);
  const [cardNumber, setCardNumber] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [tokenAddress, setTokenAddress] = useState<string | undefined>();

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [canRetry, setCanRetry] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [noticeMsg, setNoticeMsg] = useState("");
  // Set when the preflight answers "activate". Drives the step counter and the success copy —
  // that path creates no account, so no temporary password is mailed.
  const [skipsEmailGate, setSkipsEmailGate] = useState(false);
  const {
    secondsLeft,
    start: startResendCountdown,
    stop: stopResendCountdown,
  } = useResendCountdown();

  /**
   * Set once /verifyOtp succeeds. If the signup that follows it fails for its own reason ("card
   * already exists"), a retry must not re-submit the code — the server treats a re-submit as
   * idempotent, but skipping it saves a round trip and keeps the retry honest about what failed.
   */
  const emailVerifiedRef = useRef(false);

  /**
   * Bumped by reset(). Every async action captures it and drops its own setState calls if the
   * value moved, so closing the modal mid-request cannot write step 2 into a form the operator
   * already dismissed — which would reopen it on the OTP step with a blank email.
   */
  const runIdRef = useRef(0);

  const cardNumberRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const queryClient = useQueryClient();
  const networkType = useSelector(networkTypeSelector);

  const tokens = tokensFor(networkType);
  const { selected: settingsToken } = useSettlementToken();
  const tokenSupport = useTokensSupport(tokens.map((token) => token.address));
  // Activation approves only the token sent, so one the registry lacks or has paused would leave the
  // card unable to pay. Unknown support (loading/error) stays selectable.
  const isUsable = (i: number) =>
    tokenSupport[i]?.supported !== false && tokenSupport[i]?.enabled !== false;
  // Defaults to the currency active in settings until the operator picks one.
  const selectedToken = resolveToken(
    tokens.filter((_, i) => isUsable(i)),
    tokenAddress ?? settingsToken?.address,
  );

  const isStale = (run: number) => runIdRef.current !== run;

  const clearError = () => {
    setShowError(false);
    setErrorMsg("");
    setCanRetry(true);
  };

  const openError = (msg: string, retryable = true) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setCanRetry(retryable);
    setShowError(true);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessMsg("");
  };

  const reset = () => {
    // Anything already awaiting a response belongs to the previous run from here on.
    runIdRef.current += 1;
    setStep(1);
    setCardNumber("");
    setEmail("");
    setOtp("");
    setTokenAddress(undefined);
    setNoticeMsg("");
    setSkipsEmailGate(false);
    stopResendCountdown();
    emailVerifiedRef.current = false;
    // Overlays too: an error or loading modal left standing outlives the form that opened it,
    // and covers the page with no way to dismiss it.
    clearError();
    setShowLoading(false);
    setLoadingMsg("");
    setShowSuccess(false);
    setSuccessMsg("");
  };

  // ── Step 1 — sanity checks, then the flow they choose ──────────────────────
  // Preflight FIRST, always: the other order mails a code before discovering the card is revoked
  // or belongs to someone else.
  const submitStep1 = async () => {
    if (!cardNumber.trim()) return openError("Card number is required.");
    if (!EMAIL_RE.test(email))
      return openError("Please enter a valid email address.");
    if (!selectedToken) return openError("Please select a currency.");

    const run = runIdRef.current;

    // ── Leg 1: the sanity checks ──
    let decision: "activate" | "verify_email";
    try {
      setLoadingMsg("Checking card…");
      setShowLoading(true);
      const result = await card_services.prepareIssuance(
        { cardNumber: cardNumber.trim(), email: email.trim() },
        networkType,
      );
      if (isStale(run)) return;
      decision = result.decision;
    } catch (err: any) {
      if (isStale(run)) return;
      const { isTerminal, message } = classifyPreflightFailure(
        err,
        "Couldn't check that card. Please try again.",
      );
      // Terminal: the operator must edit the card number or email, which the form still holds.
      return openError(message, !isTerminal);
    }

    // Already on record for this address — a resumed issuance, no address left to prove.
    if (decision === "activate") {
      setSkipsEmailGate(true);
      setNoticeMsg("");
      setStep(3);
      return submitStep3(true);
    }

    // ── Leg 2: no cardholder yet, so prove the inbox before binding a card ──
    setSkipsEmailGate(false);
    try {
      setLoadingMsg("Sending verification code…");
      setShowLoading(true);
      await verification_services.verifyEmail({ email: email.trim() }, networkType);
      if (isStale(run)) return;
      setShowLoading(false);
      setOtp("");
      setNoticeMsg(`We sent a ${OTP_LENGTH}-digit code to ${email.trim()}.`);
      startResendCountdown();
      setStep(2);
    } catch (err: any) {
      if (isStale(run)) return;
      openError(
        apiErrorMessage(
          err,
          "Failed to send the verification code. Please try again.",
        ),
      );
    }
  };

  // ── Resend — replaces the previous code, so say so ──────────────────────────
  const handleResend = async () => {
    const run = runIdRef.current;
    clearError();
    try {
      setLoadingMsg("Sending a new code…");
      setShowLoading(true);
      await verification_services.verifyEmail({ email: email.trim() }, networkType);
      if (isStale(run)) return;
      setShowLoading(false);
      // The server overwrites the record, so the earlier code stops working immediately. Saying
      // this is what stops someone typing a slow-arriving first email and burning a guess.
      setOtp("");
      setNoticeMsg("A new code is on its way. Use the most recent email — the previous code no longer works.");
      startResendCountdown();
      emailVerifiedRef.current = false;
    } catch (err: any) {
      if (isStale(run)) return;
      const { code, message } = classifyOtpFailure(
        err,
        "Failed to resend the code. Please try again.",
      );
      // The server's own 60s floor. Keep the countdown running rather than letting the button
      // sit enabled while every press is refused.
      if (code === "RATE_LIMITED" && secondsLeft === 0) {
        startResendCountdown();
      }
      // Not retryable through the modal: its retry runs handleSubmit, which would submit the OTP
      // form rather than resend. The resend control is already on screen.
      openError(message, false);
    }
  };

  // ── Step 2 — check the code, then sign the cardholder up ────────────────────
  const submitStep2 = async () => {
    if (otp.trim().length !== OTP_LENGTH)
      return openError(`Please enter the ${OTP_LENGTH}-digit code.`);

    const run = runIdRef.current;

    // ── Leg 1: the code. Only here can a failure mean "wrong/dead OTP". ──
    if (!emailVerifiedRef.current) {
      try {
        setLoadingMsg("Verifying code…");
        setShowLoading(true);
        await verification_services.verifyOtp(
          { email: email.trim(), otp: otp.trim() },
          networkType,
        );
        if (isStale(run)) return;
        emailVerifiedRef.current = true;
      } catch (err: any) {
        if (isStale(run)) return;
        const { isWrongCode, needsNewCode, message } = classifyOtpFailure(
          err,
          "Failed to verify the code. Please try again.",
        );
        if (isWrongCode) {
          // Clear it: the modal's retry would resend the same wrong digits and spend another of
          // the five server-side guesses.
          setOtp("");
          otpRef.current?.focus();
          return openError(message, false);
        }
        if (needsNewCode) {
          setOtp("");
          stopResendCountdown();
          setNoticeMsg("That code can no longer be used. Request a new one.");
          return openError(message, false);
        }
        return openError(message);
      }
    }

    // ── Leg 2: signup. A failure here is NOT an OTP problem, and must not be read as one —
    // RATE_LIMITED here is Firebase Auth throttling, and treating it as the guess cap would throw
    // away a verification that is still valid.
    try {
      setLoadingMsg("Signing up cardholder…");
      setShowLoading(true);
      await card_services.signUpUser(
        { cardNumber: cardNumber.trim(), email: email.trim() },
        networkType,
      );
      if (isStale(run)) return;
      setShowLoading(false);
      setNoticeMsg("");
      stopResendCountdown();
      setStep(3);
      submitStep3();
    } catch (err: any) {
      if (isStale(run)) return;
      const { needsNewCode, isTerminal, message } = classifySignupFailure(
        err,
        "Failed to sign up the cardholder. Please try again.",
      );
      if (needsNewCode) {
        // The verification lapsed or was spent — re-verifying is the only way forward.
        setOtp("");
        stopResendCountdown();
        emailVerifiedRef.current = false;
        setNoticeMsg("That code can no longer be used. Request a new one.");
        return openError(message, false);
      }
      openError(message, !isTerminal);
    }
  };

  // ── Step 3 — activate the card ─────────────────────────────────────────────
  // `skipGate` is explicit for submitStep1, which sets the state and calls straight in within the
  // same render, where the state variable is still `false`. Other callers get the default.
  const submitStep3 = async (skipGate = skipsEmailGate) => {
    if (!selectedToken) return openError("Please select a currency.");
    const run = runIdRef.current;
    try {
      setLoadingMsg("Activating card…");
      setShowLoading(true);

      await card_services.updateCard(
        { cardNumber: cardNumber.trim() },
        networkType,
        selectedToken.address,
      );
      if (isStale(run)) return;

      queryClient.invalidateQueries({ queryKey: ["cards"] });
      queryClient.invalidateQueries({ queryKey: ["balances", "cards"] });

      setShowLoading(false);
      // Only a signup mails a temporary password; a resumed issuance must not promise one.
      setSuccessMsg(
        skipGate
          ? "Card has been activated successfully. It was already on the cardholder's account."
          : "Card has been activated successfully. A temporary password was sent to the cardholder's email.",
      );
      setShowSuccess(true);
    } catch (err: any) {
      if (isStale(run)) return;
      openError(
        apiErrorMessage(err, "Failed to activate card. Please try again."),
      );
    }
  };

  const handleSubmit = async () => {
    // Any new attempt supersedes the last failure. Without this a successful retry leaves the
    // error modal standing, and since it is only ever dismissed by its own Close button it
    // outlives reset() and covers the page after an operation that actually worked.
    clearError();
    if (step === 1) await submitStep1();
    else if (step === 2) await submitStep2();
    else await submitStep3();
  };

  /**
   * The error modal's retry. Dismisses the modal BEFORE resubmitting, because a retry that
   * succeeds advances the step — and a modal still open at that point has its "Try again" bound
   * to the NEXT step's action, which at step 2 means a signup error's retry would activate the
   * card instead.
   */
  const handleRetry = async () => {
    clearError();
    await handleSubmit();
  };

  const focusNext =
    (ref: React.RefObject<HTMLInputElement>) => (e: React.KeyboardEvent) => {
      if (e.key === "Enter") ref.current?.focus();
    };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const currencyOptions: DropdownOption<string>[] = tokens.map((token, i) => ({
    value: token.address,
    label: tokenLabel(token),
    keywords: token.address,
    disabled: !isUsable(i),
  }));

  const currencyField: CustomFormSelectField<string> = {
    kind: "select",
    name: "currency",
    label: "Currency",
    value: selectedToken?.address ?? null,
    onChange: setTokenAddress,
    options: currencyOptions,
    placeholder: "Select a currency",
    searchPlaceholder: "Search currencies…",
  };

  const step1Fields: CustomFormField[] = [
    {
      kind: "text",
      name: "cardNumber",
      label: "Card Number",
      value: cardNumber,
      onChange: setCardNumber,
      onKeyDown: focusNext(emailRef),
      placeholder: "XXXX XXXX XXXX XXXX",
      autoComplete: "off",
      inputRef: cardNumberRef,
    },
    {
      kind: "email",
      name: "email",
      label: "Email",
      value: email,
      onChange: setEmail,
      onKeyDown: submitOnEnter,
      placeholder: "cardholder@email.com",
      autoComplete: "email",
      inputRef: emailRef,
    },
    currencyField,
  ];

  const step2Fields: CustomFormField[] = [
    {
      kind: "email",
      name: "email",
      label: "Email",
      value: email,
      onChange: () => {},
      disabled: true,
      autoComplete: "off",
    },
    {
      kind: "text",
      name: "otp",
      label: "Verification Code",
      value: otp,
      // Digits only, capped at OTP_LENGTH — the field cannot hold a value the server would reject
      // for shape, so a wasted guess can only come from a genuinely wrong code.
      onChange: (val) =>
        setOtp(sanitizeOtp(val)),
      onKeyDown: submitOnEnter,
      placeholder: "12345",
      autoComplete: "one-time-code",
      inputRef: otpRef,
    },
  ];

  const step3Fields: CustomFormField[] = [
    {
      kind: "text",
      name: "cardNumber",
      label: "Card Number",
      value: cardNumber,
      onChange: () => {},
      disabled: true,
      autoComplete: "off",
    },
    {
      kind: "email",
      name: "email",
      label: "Email",
      value: email,
      onChange: () => {},
      disabled: true,
      autoComplete: "off",
    },
    { ...currencyField, disabled: true },
  ];

  const fieldsByStep: Record<IssueCardStep, CustomFormField[]> = {
    1: step1Fields,
    2: step2Fields,
    3: step3Fields,
  };

  const submitLabelByStep: Record<IssueCardStep, string> = {
    // Not "Send Code" — whether a code is sent is the preflight's answer.
    1: "Continue",
    2: "Verify & Sign Up",
    3: "Activate",
  };

  const isSubmitDisabled =
    step === 1
      ? !cardNumber.trim() || !EMAIL_RE.test(email) || !selectedToken
      : step === 2
        ? otp.length !== OTP_LENGTH
        : false;

  const totalSteps = skipsEmailGate ? 2 : 3;
  const displayStep = skipsEmailGate && step === 3 ? 2 : step;

  return {
    step,
    displayStep,
    totalSteps,
    fields: fieldsByStep[step],
    submitRef,
    submitLabel: submitLabelByStep[step],
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    canRetry,
    showSuccess,
    successMsg,
    noticeMsg,
    skipsEmailGate,
    canResend: step === 2 && secondsLeft === 0,
    secondsLeft,
    handleResend,
    handleSubmit,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
    reset,
  };
}
